import styles from './popup.module.scss';
import {
  useEffect,
  useRef,
  useState
} from "react";
import ReactDOM from "react-dom";
import { BoundingClientRectObserver } from "@html-ng/bounding-client-rect-observer";
import { CSSTransition } from "react-transition-group";

type PopupProps = {
  parent: HTMLDivElement | null;
  isShow: boolean;
  onClose?: (e?: MouseEvent) => void;
  child: JSX.Element | string;
  width?: number;
  top?: number;
  left?: number;
  classContainer?: string;
}

export const Popup = ({ isShow, onClose, child, left, top, width, parent, classContainer }: PopupProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [offset, setOffset] = useState<{ left: number; top: number }>();
  const [isParentVisible, setIsParentVisible] = useState<boolean>(true);

  useEffect(() => {
    if (!parent) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsParentVisible(entry.isIntersecting);
      },
      { threshold: 0.1 } // Adjust threshold as needed
    );

    observer.observe(parent);

    return () => {
      observer.disconnect();
    };
  }, [parent]);

  useEffect(() => {
    if (!isParentVisible && onClose) {
      onClose();
    }
  }, [isParentVisible, onClose]);

  useEffect(() => {
    if (isShow && isParentVisible) {
      setTimeout(() => {
        window.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("resize", resizeWindow);
      });
      calculateOffset();
    } else {
      setOffset(undefined);
    }
    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", resizeWindow);
    };
  }, [isShow, isParentVisible]);

  useEffect(() => {
    const observer = new BoundingClientRectObserver(calculateOffset)
    if (!!parent) {
      observer.observe(parent);
    }
    return () => {
      observer.disconnect();
    }
  }, [parent]);

  useEffect(() => {
    calculateOffset();
  }, [child]);

  const resizeWindow = (): void => {
    calculateOffset();
  };

  const calculateOffset = (): void => {
    const newOffset = getOffset(parent);
    if (!!parent) {
      const bottomTop = newOffset.top + parent.offsetHeight;
      const containerHeight = containerRef.current?.clientHeight as number;
      if (bottomTop + containerHeight > window.innerHeight) {
        newOffset.top -= containerHeight;
      } else {
        newOffset.top = bottomTop;
      }
    }
    if (!!left) {
      newOffset.left += left;
    }
    if (!!top) {
      newOffset.top += top;
    }
    setOffset(newOffset);
  };

  const getOffset = (el?: HTMLElement | null) => {
    const rect = el?.getBoundingClientRect();
    return { top: rect?.top || 0, left: rect?.left || 0 };
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (!!onClose && containerRef.current && !containerRef.current.contains(e.target as Node)) {
      onClose(e);
    }
  };

  const getContainerStyle = (): string => {
    let result = styles.container;
    if (!!classContainer) {
      result += ` ${classContainer}`;
    }
    return result;
  };

  return ReactDOM.createPortal(
    <CSSTransition in={isShow && isParentVisible} timeout={300} unmountOnExit nodeRef={containerRef} classNames={{
      enter: styles.enter,
      exit: styles.exit,
      enterActive: styles.enterActive,
      exitActive: styles.exitActive,
    }}>
      <div className={getContainerStyle()}
           style={{ top: offset?.top, left: offset?.left, width: width }}
           ref={containerRef}>
        {child}
      </div>
    </CSSTransition>,
    document.body
  );
}
